import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import Icon from "@/components/icons";
import { DialogPortal, DialogOverlay } from ".";
import useTranslations from "@/i18n/useTranslations";

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  roundedCloseButton?: boolean;
};

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, roundedCloseButton = false, ...props }, ref) => {
  const t = useTranslations("Dialog");
  const closeLabel = t("close");
  const roundedCloseButtonClass = roundedCloseButton
    ? "absolute bg-white text-primary right-2.5 top-2.5 inline-flex text-xl size-[35px] m-2 appearance-none items-center justify-center rounded-full border border-2 hover:bg-primary hover:text-white focus:outline-none"
    : "absolute text-primary right-4 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground";
  return (
    <DialogPortal>
      <DialogOverlay>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "relative z-[2147483647] w-full max-w-3xl gap-4 border bg-background p-6  duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded",
            className,
          )}
          {...props}
        >
          {children}
          <DialogPrimitive.Close
            className={roundedCloseButtonClass}
            aria-label={closeLabel}
          >
            <Icon
              type="close-material"
              pathProps={{
                width: "24px",
                height: "24px",
              }}
              svgProps={{
                width: "24px",
                height: "24px",
              }}
            />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
